import React, { useState, useEffect, useRef } from "react";
import BackButton from "../../../Components/Buttons/Variants/BackButton";
import Button from "../../../Components/Buttons/Button";
import Text from "../../../Components/Text";
import RoleGuard from "../../../Components/RoleGuard";
import { useOrientation } from "../../../Components/OrientationChecker";
import ItemScrollbar from "../../../Components/ItemScrollbar";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { useSumUpPaymentHandler } from "../../../Components/SumUpPaymentHandler";
import { useModal } from "../../../Components/Modal";
import Snackbar from "../../../Components/Elements/Snackbar";
import TIcon from "../../../Components/TIcon";
import ButtonWrapper from "./ButtonWrapper";
import QRCodeScanner from "./QRCodeScanner";
import QRCodeGenerator from "../../../Helper/QRCodeGenerator";
import { Orders, createOrder, calculateOrderPrice, updateOrder, getAllOrders, updateOrderStatus, getOrderById } from "../../../Model/Orders";
import { getAllCategories } from "../../../Model/Categories";
import { getProductsByCategorie } from "../../../Model/Products";
import { getAllDiscounts } from "../../../Model/Discounts";
import { getAllExtras } from "../../../Model/Extras";
import { getOfferByCode } from "../../../Model/Offers";
import { dbEvents } from "../../../db/dbEvents";
import { getWorkspace } from "../../../Model/Workspace";
import { WorkerLogin } from "./WorkerLogin";
import { findByUserCodeNoAccounted, saveOfflineRegisterEntries, getOffllineRegisterEntries, updateEntryBalance, getRegisterEntriesByOfflineId } from "../../../Model/RegisterEntries";

const Counter = () => {
    /* Modal Loader */
    const { ModalView, openModal, closeModal } = useModal()
    const [workspace, setWorkspace] = useState(null)
    useEffect(() => {
        async function saveWorkspace() {
            const data = await getWorkspace()
            setWorkspace(data)
        }
        saveWorkspace()
    }, [])

    const subscription = workspace?.subscription;
    const isTester = workspace?.freeTester;
    const currency = workspace?.currency

    function isAllowed(target) {
        return isTester || (subscription && subscription[target])
    }

    const [isLoginModal, setIsLoginModal] = useState(false)
    const { isPortrait } = useOrientation();
    const paymentHandlerRef = useRef(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const isCardPaymentAllowed = isAllowed("useCard") && workspace?.allowCardPayment
    const [user,] = useState(JSON.parse(localStorage.getItem("user")))

    // Schema imports
    const { create, load, addProduct } = Orders()

    /* FormObject for Order Management */
    let [order, setOrder] = useState(() => create());

    /* Objects */
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null)
    const selectedCategoryRef = useRef(selectedCategory);
    const [products, setProducts] = useState([])
    const [extras, setExtras] = useState([])
    const [discounts, setDiscounts] = useState([])
    const [selectedOrderItem, setSelectedOrderItem] = useState(null);
    const [onDiscountMenu, setOnDiscountMenu] = useState(false)

    /* Variables for cancel and historic order list */
    const [historicalOrders, setHistoricalOrders] = useState([])
    const [orderIndex, setOrderIndex] = useState(-1)

    /* input and cashier methods */
    const [inputValue, setInputValue] = useState(null)

    /* Force re-render trigger */
    const [, forceUpdate] = useState({});
    const triggerUpdate = () => forceUpdate({});

    const [cashierId, setCashierId] = useState(null)
    // UserLogin when activated
    useEffect(() => {
        if (isAllowed("allowCashierUserTracking") && workspace.cashierUserTracking) {
            setIsLoginModal(true)
            openModal(<WorkerLogin afteraction={(user) => findOrCreateCashRegisterEntry(user)} />)
        }
    }, [workspace])

    async function findOrCreateCashRegisterEntry(user) {
        const data = await findByUserCodeNoAccounted(user)
        if (!data) {
            const startTime = new Date().toISOString().slice(0, -1)
            const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            const newEntry = {
                startTime: startTime,
                startAmount: null,
                user: user,
                finalAmount: workspace?.cashRegisterStartMoney,
                offlineId: offlineId
            }
            const old = await getOffllineRegisterEntries()
            old.push(newEntry)
            await saveOfflineRegisterEntries(old)
            setCashierId(offlineId)
        } else {
            setCashierId(data.offlineId)
        }
        setIsLoginModal(false)
        closeModal()
    }

    //Live Refresh Hook
    useEffect(() => {
        fetchCategoryData(true)
        fetchExtraData()
        fetchDiscountData()
        fetchStatisticData()
        const handleChange = (e) => {
            if (["categories", "sync_categories"].includes(e.detail.store)) {
                fetchCategoryData()
            }
            if (["products", "sync_products"].includes(e.detail.store)) {
                if (selectedCategoryRef.current) {
                    fetchProductsById(selectedCategoryRef.current)
                }
            }
            if (["extras", "sync_extras"].includes(e.detail.store)) {
                fetchExtraData()
            }
            if (["discounts", "sync_discounts"].includes(e.detail.store)) {
                fetchDiscountData()
            }
            if (["orders", "sync_orders"].includes(e.detail.store)) {
                fetchStatisticData()
            }
        };
        dbEvents.addEventListener("dbChange", handleChange);

        return () => {
            dbEvents.removeEventListener("dbChange", handleChange);
        };
    }, []);

    //on category change
    useEffect(() => {
        if (selectedCategory) {
            fetchProductsById()
        }
    }, [selectedCategory]);

    useEffect(() => {
        selectedCategoryRef.current = selectedCategory;
    }, [selectedCategory]);

    //on index change go trough the last orders
    useEffect(() => {
        if (orderIndex > -1 && orderIndex < historicalOrders.length) {
            loadHistoricalOrder(historicalOrders[orderIndex]);
        }
    }, [orderIndex]);

    useEffect(() => {
        paymentHandlerRef.current = async () => {
            await handleManualPayment();
        }
    }, [order]);

    
    const orderCreator = async (orderDTO) => {
        const orderId = order.get("orderId")

        if (orderId) {
            await updateOrder(orderDTO, orderId)
        } else {
            const createdOrder =await createOrder(orderDTO)
            
            if (createdOrder && createdOrder.offlineId) {
                order.set("offlineId", createdOrder.offlineId)
                triggerUpdate()
            }
        }
    }

    // Card Payment handler (SumUp integration)
    const { handlePayment } = useSumUpPaymentHandler({
        succsessAction: () => {
            if (paymentHandlerRef.current) {
                paymentHandlerRef.current()
            }
        },
        errorAction: () => console.error("error")
    })

    // Card Payment handler (Manual integration)
    const handleManualPayment = async () => {
        const orderDTO = createOrderDTO("CARD");
        await orderCreator(orderDTO);
        setInputValue(0)
        order.set('isReady', false);
        fetchStatisticData()
        resetOrder();
        triggerUpdate();
    }

    // Modal Opener for manual card payment
    const openManualPaymentModal = (price) => {
        openModal(
            <div className="flex flex-col gap-2 p-4">
                <Text type="main" classNames="text-center">Kartenzahlung</Text>
                <Text type="subtitle" classNames="text-center">Gesamtbetrag: <b>{price.toFixed(2)}</b> {currency}</Text>
                <Text type="subtitle" classNames="text-center">Bitte bestätigen Sie die Zahlung sobald diese abgeschlossen ist.</Text>
                <Button text={"Zahlung bestätigen"} classNames="bg-lime-500" onClickAction={async () => {
                    await handleManualPayment(); closeModal();
                }} />
            </div>
        )
    }

    // FUNCTIONS 
    //Select Category
    const handleCategoryClick = async (category) => {
        setSelectedCategory(category)
        handleOutsideClick()
    }

    //Fetch helper
    const fetchCategoryData = async (setOnRecieve = false) => {
        const data = await getAllCategories()
        setCategories(data)
        if (setOnRecieve) {
            if (data.length > 0) {
                setSelectedCategory(data[0])
            }
        }
    };

    const fetchProductsById = async (category = selectedCategory) => {
        const products = await getProductsByCategorie(category.id)
        setProducts(products)
    }

    const fetchExtraData = async () => {
        const data = await getAllExtras()
        setExtras(data)
    }

    const fetchDiscountData = async () => {
        const data = await getAllDiscounts()
        setDiscounts(data)
    }

    const fetchStatisticData = async () => {
        const data = await getAllOrders()
        setOrderIndex(-1)
        order.set('isPreview', false);
        setHistoricalOrders(data.reverse());
    };

    const loadHistoricalOrder = (selectedOrder) => {
        const newOrder = load(selectedOrder);
        setOrder(newOrder);
        setInputValue(null);
        triggerUpdate();
    };

    const addDiscountToOrder = (discount) => {
        order
            .set('discount', discount)
            .set('discountId', discount?.id || null)
            .set('isReady', false)
            .set('totalPrice', null);

        setSelectedOrderItem(null);
        setInputValue(null);
        triggerUpdate();
    }

    const addProductToOrder = (product) => {
        if (order.get('isFinal')) {
            resetOrder();
        }

        const currentProducts = order.get('orderProducts')
        const currentCount = currentProducts
            .filter(p => p.productId === product.id)
            .reduce((sum, p) => sum + p.count, 0);
        const useStockManagement = (isAllowed("useInventoryManagement") && product.availableStock !== null && product.stockManaged)
        const inputQuantity = inputValue ? (parseInt(inputValue, 10) === 0 ? 1 : parseInt(inputValue, 10)) : 1;
        if (useStockManagement && (inputQuantity + currentCount) > product.availableStock) {
            setSnackbarMessage("Nicht genügend Produkte im Inventar")
        }
        const quantity = useStockManagement ? (inputQuantity + currentCount) > product.availableStock ? currentCount === product.availableStock ? 0 : product.availableStock : inputQuantity : inputQuantity
        if (quantity > 0) {
            setOrder(addProduct(order, product, quantity))
        }


        resetOrderInputs();
        order.set('isPreview', false);
        triggerUpdate();
    };

    const addExtraToProduct = (extra) => {
        if (!selectedOrderItem) return;

        const availableQuantity = selectedOrderItem.count;
        const selectedQuantity = inputValue ? (parseInt(inputValue, 10) === 0 ? 1 : parseInt(inputValue, 10)) : 1;
        const quantityToUse = Math.min(availableQuantity, selectedQuantity);

        if (selectedOrderItem.extras.some(existingExtra => existingExtra.id === extra.id)) {
            return;
        }

        const newProductWithExtra = {
            ...selectedOrderItem,
            count: quantityToUse,
            extras: [...(selectedOrderItem.extras || []), extra],
            totalPrice: (selectedOrderItem.unitPrice + extra.price) * quantityToUse
        };

        const currentProducts = order.get('orderProducts');
        let foundMatchingProduct = false;

        const updatedProducts = currentProducts.map((item) => {
            if (item.productId === selectedOrderItem.productId && item.extras === selectedOrderItem.extras) {
                return {
                    ...item,
                    count: item.count - quantityToUse,
                    totalPrice: item.unitPrice * (item.count - quantityToUse)
                };
            }

            if (item.productId === newProductWithExtra.productId && areExtrasEqual(item.extras, newProductWithExtra.extras)) {
                foundMatchingProduct = true;
                return {
                    ...item,
                    count: item.count + quantityToUse,
                    totalPrice: (item.unitPrice + extra.price) * (item.count + quantityToUse)
                };
            }

            return item;
        }).filter(item => item.count > 0);

        const finalProducts = foundMatchingProduct ? updatedProducts : [...updatedProducts, newProductWithExtra];

        order.set('orderProducts', finalProducts);
        resetOrderInputs();
        setOrderIndex(-1);
        order.set('isPreview', false);
        triggerUpdate();
    };

    const handleOrderTypeClick = (type) => {
        order.set('type', type);
        triggerUpdate();
    }

    const resetOrderInputs = () => {
        setSelectedOrderItem(null);
        setInputValue(null);
        order
            .set('isReady', false)
            .set('totalPrice', null)
            .set('status', null)
        triggerUpdate();
    }

    const handleOutsideClick = () => {
        setSelectedOrderItem(null);
        setOnDiscountMenu(false);
        order
            .set('isReady', false)
            .set('totalPrice', null);
        triggerUpdate();
    }

    const resetOrder = () => {
        resetOrderInputs();
        order.reset();
        setOrderIndex(-1);
        triggerUpdate();
    }

    const handleDiscountMenuClick = () => {
        setOnDiscountMenu(true);
    }

    const handleCurrencyClick = (e, amount) => {
        e.stopPropagation();
        const add = Math.round(amount);
        const current = parseInt(inputValue || 0, 10);
        const newValue = current + add;
        setInputValue(newValue);
    }

    const setTotalInpuValue = (e, amount) => {
        e.stopPropagation();
        const value = parseInt(Math.round(amount) || 0, 10);
        setInputValue(value);
    }

    const handleAlreadyPayed = async (e) => {
        e.stopPropagation();
        const currentProducts = order.get('orderProducts');
        const coupons = order.get("coupons")
        if ((currentProducts.length === 0 && coupons.lenght === 0) || order.get('isFinal')) { return }

        const orderDTO = createOrderDTO("PAYED_INSIDE");
        await orderCreator(orderDTO);

        setInputValue(order.get('totalPrice'));
        order
            .set('isReady', false)
            .set('isFinal', true);

        fetchStatisticData();
        triggerUpdate();
    }

    const handleUnpayed = async (e) => {
        e.stopPropagation();
        const currentProducts = order.get('orderProducts');
        const coupons = order.get("coupons")
        if ((currentProducts.length === 0 && coupons.lenght === 0) || order.get('isFinal')) { return }

        const orderDTO = createOrderDTO("UNPAYED");
        await orderCreator(orderDTO);

        setInputValue(order.get('totalPrice'));
        order
            .set('isReady', false)

        fetchStatisticData();
        triggerUpdate();
    }

    const handleOldOrdersClick = (backward) => {
        if (historicalOrders.length > 0) {
            if (backward) {
                order
                    .set('isPreview', true)
                    .set('status', null);
                triggerUpdate();
            } else {
                resetOrder();
            }
        }
    }

    const setOldOrderPreviewClick = (index) => {
        order.set('isPreview', false);
        setOrderIndex(index);
        triggerUpdate();
    }

    const handleCancelOrder = async () => {
        const orderId = order.get('orderId');
        const totalPrice = Number(Number(order.get('totalPrice')).toFixed(2));

        // If keeping Track of amount in cash register
        if (cashierId && isAllowed("allowCashierUserTracking") && workspace.cashierUserTracking) {
            const oldData = await getRegisterEntriesByOfflineId(cashierId)
            const oldValue = oldData.finalAmount
            await updateEntryBalance((oldValue - totalPrice), cashierId)
        }
        if (orderId) {
            await updateOrderStatus(orderId, "CANCELED");
            resetOrder();
            fetchStatisticData();
        }
    }

    const createOrderDTO = (paymentType, isWaste = false) => {
        const currentProducts = order.get('orderProducts');
        const currentOffers = order.get('coupons');
        const discountId = order.get('discountId');
        const dto = {
            type: order.get('type'),
            paymentType: paymentType,
            createdBy: user.email,
            status: "PENDING",
            orderProducts: currentProducts.map(item => ({
                productId: item.productId,
                name: item.name,
                price: item.unitPrice,
                buyingPrice: item.buyingprice,
                mwst: item.mwst,
                toGomwst: item.toGomwst,
                reduceable: item.reduceable,
                count: item.count,
                extras: item.extras.map(extra => ({
                    id: extra.id,
                    name: extra.name,
                    price: extra.price
                }))
            })),
            coupons: currentOffers
        };

        if (discountId) {
            dto.discountId = discountId;
        }

        if (isWaste) {
            dto.status = "WASTE";
        }

        return dto;
    }

    const handleWasteClick = async () => {
        const currentProducts = order.get('orderProducts');
        if (currentProducts.length == 0 || order.get('isFinal')) { return }

        const orderDTO = createOrderDTO("ON_COUNTER", true);
        await orderCreator(orderDTO);

        setInputValue("Waste: " + order.get('totalPrice'));
        order
            .set('isReady', false)
            .set('isFinal', true);

        fetchStatisticData();
        triggerUpdate();
    }

    const handleTotalClick = async (alreadyPayed = false, amount = null) => {
        const currentProducts = order.get('orderProducts');
        const currentOffers = order.get('coupons');

        if ((currentProducts.length === 0 && currentOffers.length === 0) || order.get('isFinal')) {
            return;
        }

        const orderDTO = createOrderDTO(alreadyPayed ? "PAYED_INSIDE" : "ON_COUNTER");

        if (order.get('isReady')) {
            const payedAmount = parseFloat(inputValue) / 100;
            const totalPrice = Number(Number(order.get('totalPrice')).toFixed(2));

            if (payedAmount >= totalPrice || amount >= totalPrice) {
                const change = amount ? (amount - totalPrice) : (payedAmount - totalPrice);

                // If keeping Track of amount in cash register
                if (cashierId && isAllowed("allowCashierUserTracking") && workspace.cashierUserTracking) {
                    const oldData = await getRegisterEntriesByOfflineId(cashierId)
                    const oldValue = oldData.finalAmount
                    await updateEntryBalance((oldValue + totalPrice), cashierId)
                }

                await orderCreator(orderDTO);
                setInputValue(change.toFixed(2));
                order
                    .set('isReady', false)
                    .set('isFinal', true)
                    .set('status', "PENDING");
                triggerUpdate();
            }
        } else {
            const response = await calculateOrderPrice(orderDTO);
            order
                .set('totalPrice', parseFloat(response))
                .set('isReady', true);
            triggerUpdate();
        }

        fetchStatisticData();
    }

    const handleQRCodeClick = () => {
        openModal(<QRCodeScanner onConfirmAction={handleQRCodeConfirm} />)
    }

    const handleQRCodeConfirm = async (code) => {
        const data = await getOfferByCode(code);
        if (data) {
            order.set('coupons', [data]);
            triggerUpdate();
        }
        closeModal();
    }

    const getStatusLabel = () => {
        const status = order.get('status');
        if (status == null) { return }
        if (status === "PENDING") { return "Offen" }
        if (status === "COMPLETE") { return "Abgeschlossen" }
        if (status === "CANCELED") { return "Storniert" }
        if (status === "WASTE") { return "Waste" }
    }

    const getPaymentLabel = () => {
        const status = order.get('paymentType');
        if (status == null) { return }
        if (status === "CARD") { return "Karte" }
        if (status === "ON_COUNTER") { return "Bar Bezahlt" }
        if (status === "PAYED_INSIDE") { return "Bereits bezahlt" }
        if (status === "UNPAYED") { return "Unbezahlt" }
    }

    const inputNumber = (value) => {
        setInputValue((prev) => `${prev || ""}${value}`);
    }

    const areExtrasEqual = (extrasA, extrasB) => {
        if (extrasA.length !== extrasB.length) return false;
        return extrasA.every(extraA =>
            extrasB.some(extraB => extraA.id === extraB.id)
        );
    };

    const clearInput = () => {
        if (selectedOrderItem) {
            const currentProducts = order.get('orderProducts');
            const updatedProducts = currentProducts.map(item => {
                if (item.productId === selectedOrderItem.productId && areExtrasEqual(item.extras, selectedOrderItem.extras)) {
                    const removeAmount = inputValue ? parseInt(inputValue, 10) : 1;
                    const newCount = item.count - removeAmount;
                    return newCount > 0
                        ? { ...item, count: newCount, totalPrice: newCount * item.unitPrice }
                        : null;
                }
                return item;
            }).filter(Boolean);

            order.set('orderProducts', updatedProducts);
            setSelectedOrderItem(null);
            order.set('totalPrice', null);
            triggerUpdate();
        }
        setInputValue(null);
        order.set('isReady', false);
        triggerUpdate();
    };

    // UI INTERFACE
    const ProductButton = ({ product, action = null, background = "bg-gray-300", isDiscount = false, hashColor = null, outOfStock = false }) => (
        <Button classNames="w-full" buttonColor={hashColor ? null : background} style={{ backgroundColor: hashColor }} children={
            <div className="flex flex-col justify-between p-1 h-full w-full">
                {
                    isDiscount ? (
                        <Text type="orderitem" classNames="w-full text-center">{product.percentage} %</Text>
                    ) : (
                        <>
                            <Text type="orderitem" classNames="w-full text-wrap line-clamp-2 break-all">{product.name}</Text>
                            <Text type="orderitem" classNames="w-full text-center">{product.price} {currency}</Text>
                            {outOfStock && (
                                <Text type="orderitem" classNames="w-full text-center">
                                    Ausverkauft
                                </Text>)}
                        </>
                    )}
            </div>
        } onClickAction={(e) => {
            e.stopPropagation();
            if (action) action();
        }} />
    )

    const AdditionalButton = ({ text, action = null, background = "bg-gray-300" }) => (
        <Button classNames="w-full" buttonColor={background} children={
            <div className="flex flex-col justify-between p-1 h-full w-full">
                <Text type="orderitem" classNames="w-full text-center">{text}</Text>
            </div>
        } onClickAction={(e) => {
            e.stopPropagation();
            if (action) action(e);
        }} />
    )

    const productOverview = () => (
        <ButtonWrapper children={
            products.length > 0 ? products.map((product, index) => (
                <ProductButton key={product.id || index} product={product} action={() => {
                    if (!(product.availableStock != null && product.availableStock <= 0)) {
                        addProductToOrder(product)
                    }
                }
                }
                    hashColor={product.colorCode ? product.colorCode : selectedCategory.colorCode ? selectedCategory.colorCode : null}
                    outOfStock={(product.availableStock != null && product.availableStock <= 0)} />
            )) : null
        } />
    )

    const extraOverview = () => (
        <ButtonWrapper children={extras.length > 0 ? extras.map((extra, index) => (
            <ProductButton key={extra.id || index} product={extra} action={() => addExtraToProduct(extra)} background="bg-green-200" />
        )) : null} />
    )

    const discountOverview = () => (
        <ButtonWrapper>
            {discounts.length > 0 ? discounts.map((discount, index) => (
                <ProductButton key={discount.id || index} product={discount} action={() => addDiscountToOrder(discount)} background="bg-green-200" isDiscount={true} />
            )) : null}
            <AdditionalButton
                key="remove-discount"
                text={"Rabatt entfernen"}
                background="bg-red-200"
                action={() => { addDiscountToOrder(null) }}
            />
        </ButtonWrapper>
    );

    const paymentButton = (amount) => (
        <AdditionalButton
            key={`payment-${amount}`}
            text={`${amount}${currency}`}
            background="bg-lime-400"
            action={(e) => { handleCurrencyClick(e, (amount * 100)) }}
        />
    )

    const totalOverview = () => (
        <ButtonWrapper>
            {paymentButton(5)}
            {paymentButton(10)}
            {paymentButton(20)}
            {paymentButton(50)}
            {paymentButton(100)}
            {paymentButton(200)}
            {window.AndroidBridge && isCardPaymentAllowed && (
                <AdditionalButton
                    key="card"
                    text={"Karte (SumUp)"}
                    background="bg-lime-400"
                    action={() => { handlePayment(Number(order.get('totalPrice'))) }}
                />
            )}
            {isCardPaymentAllowed && (
                <AdditionalButton
                    key="card"
                    text={"Karte (Manuell)"}
                    background="bg-lime-400"
                    action={() => { openManualPaymentModal(Number(order.get('totalPrice'))) }}
                />
            )}
            <AdditionalButton
                key="exact"
                text={"Exakt"}
                background="bg-lime-400"
                action={(e) => { setTotalInpuValue(e, order.get('totalPrice') * 100) }}
            />
            <AdditionalButton
                key="already-paid"
                text={"Schon Bezahlt"}
                background="bg-blue-200"
                action={(e) => { handleAlreadyPayed(e) }}
            />
            <AdditionalButton
                key="unpayed"
                text={"Noch nicht bezahlen"}
                background="bg-blue-200"
                action={(e) => { handleUnpayed(e) }}
            />
            <RoleGuard>
                <AdditionalButton
                    key="waste"
                    text={"Waste"}
                    background="bg-red-200"
                    action={() => { handleWasteClick() }}
                />
            </RoleGuard>
        </ButtonWrapper>
    )

    const productTerminal = () => (
        <div className={` ${isPortrait ? 'w-full h-1/3 max-h-1/3' : 'w-3/5 h-full'} flex flex-col px-2 gap-2`}>
            <div className="h-fit flex flex-row justify-between gap-1 ">
                <div className="h-full flex items-center justify-center">
                    <BackButton buttonSize="h-10 w-10" />
                </div>

                <ItemScrollbar backgroundClasses="bg-gray-100" alwaysColored={true} itemType={"Kategorien"} items={categories} onClickAction={handleCategoryClick} itemDisplayOption={(item) => item.name} colorDisplayOption={(item) => item.colorCode} />
                <div className="h-full flex items-center gap-2 justify-center">
                    <RoleGuard>
                        <Button classNames="flex p-3 h-10 w-10" text={"%"} onClickAction={() => handleDiscountMenuClick()} />
                    </RoleGuard>
                    <Button classNames="flex p-3 h-10 w-10" tIconName={"qrcode"} onClickAction={() => handleQRCodeClick()} />
                </div>
            </div>
            <div className="flex">
                <ToggleSwitch
                    backgroundClasses="rounded-lg bg-white p-1"
                    toggleObjects={[
                        { text: "Vor Ort", icon: "home", iconColor: "white", selectedStyle: "bg-blue-500 border-2 text-white", unselectedStyle: "bg-white", onClickAction: () => handleOrderTypeClick("ON_SPOT"), isSelected: order.get("type") === "ON_SPOT" },
                        { text: "Zum Mitnehmen", icon: "shopping-bag", iconColor: "white", selectedStyle: "bg-green-500 border-2 text-white", unselectedStyle: "bg-white", onClickAction: () => handleOrderTypeClick("TAKE_AWAY"), isSelected: order.get("type") === "TAKE_AWAY" },
                        //{ text: "Vorbestellung", selectedStyle: "bg-red-500 border-2 text-white", unselectedStyle: "bg-red-200", onClickAction: () => handleOrderTypeClick("PREORDER") }
                    ]}
                    classNames="w-full gap-1"
                />
            </div>

            {selectedOrderItem ? extraOverview() : onDiscountMenu ? discountOverview() : order.get('isReady') ? totalOverview() : productOverview()}


        </div>
    );

    const numberButton = (number) => (
        <Button text={`${number}`} classNames="text-xl w-full h-full" onClickAction={() => inputNumber(number)} />
    );

    const registerInputTerminal = () => (
        <div className="h-full w-full flex flex-col gap-1">
            <div className="w-full h-full flex flex-row gap-1">
                {numberButton(1)}
                {numberButton(2)}
                {numberButton(3)}
            </div>
            <div className="w-full h-full flex flex-row gap-1">
                {numberButton(4)}
                {numberButton(5)}
                {numberButton(6)}
            </div>
            <div className="w-full h-full flex flex-row gap-1">
                {numberButton(7)}
                {numberButton(8)}
                {numberButton(9)}
            </div>
            <div className="w-full h-full flex flex-row gap-1">
                <Button text={"Total"} classNames="text-xl w-full py-1" buttonColor="bg-lime-500" onClickAction={() => handleTotalClick()} />
                <Button text={"0"} classNames="text-xl w-full py-1" onClickAction={() => inputNumber(0)} />
                <Button text={inputValue ? "C" : "Delete"} classNames="text-xl w-full py-1" buttonColor="bg-red-500" onClickAction={() => clearInput()} />
            </div>
        </div>
    );

    const getOrderTypeColor = (orderType) => {
        if (orderType === "ON_SPOT") return "bg-blue-500";
        if (orderType === "TAKE_AWAY") return "bg-green-500";
        if (orderType === "PREORDER") return "bg-gray-500";
        return "bg-gray-300";
    };

    const formatTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    const FALLBACK_ONSITE_VAT = 8.1
    const FALLBACK_TAKEAWAY_VAT = 2.6

    const getProductVatRate = (product, orderType) => {
        if (orderType === "TAKE_AWAY") {
            if (product.toGomwst != null) return product.toGomwst
            if (product.mwst != null) return product.mwst
            return FALLBACK_TAKEAWAY_VAT
        }

        if (product.mwst != null) return product.mwst
        return FALLBACK_ONSITE_VAT
    }

    const orderListItem = () => (
        <>
            {order.get('isPreview') ? (
                <div className="flex flex-col gap-2">
                    {historicalOrders.map((histOrder, index) => (
                        <Button
                            key={histOrder.id}
                            classNames="w-full"
                            buttonColor={getOrderTypeColor(histOrder.type)}
                            onClickAction={() => setOldOrderPreviewClick(index)}
                            children={
                                <div className="flex justify-between items-center w-full py-1">
                                    <Text type="orderitem" classNames="text-white font-bold">
                                        {formatTime(histOrder.startTime)}
                                    </Text>
                                    <div className="flex items-center gap-2">
                                        {histOrder.preorderName && (
                                            <Text type="orderitem" classNames="text-white">
                                                {histOrder.preorderName}
                                            </Text>
                                        )}
                                        <Text type="orderitem" classNames="text-white font-bold">
                                            {histOrder.totalPrice.toFixed(2)} {currency}
                                        </Text>
                                        {histOrder.paymentType === "ON_COUNTER" && (
                                            <TIcon name={"cash"} />
                                        )}
                                        {histOrder.paymentType === "PAYED_INSIDE" && (
                                            <TIcon name={"building-store"} />
                                        )}
                                        {histOrder.paymentType === "UNPAYED" && (
                                            <TIcon name={"creative-commons-nc"} />
                                        )}
                                    </div>
                                </div>
                            }
                        />
                    ))}
                </div>
            ) : (
                <>
                    {order.get('orderProducts').map((item, index) => (
                        <Button
                            key={index}
                            classNames={`w-full`}
                            buttonColor="bg-white"
                            onClickAction={(e) => {
                                e.stopPropagation();
                                setSelectedOrderItem(item);
                            }}
                            borderStyle={`${selectedOrderItem === item ? "border rounded-lg border-blue-500" : "border-b border-gray-100"}`}
                            overflow=""
                            children={
                                <div className="flex flex-col w-full">
                                    <div className="flex justify-between w-full px-2">
                                        <Text type="orderitem" classNames="text-left">{item.name} x{item.count}</Text>
                                        <Text type="orderitem">{item.totalPrice.toFixed(2)} {currency}</Text>
                                    </div>
                                    {item.mwst != null && item.mwst > 0 && (
                                        <div className="w-full flex flex-row justify-between px-2 mt-1">

                                            <Text type="subtitle">Davon MWST: {getProductVatRate(item, order.get("type"))}%</Text>
                                            <Text type="subtitle">{(item.totalPrice * getProductVatRate(item, order.get("type")) / 100).toFixed(2)} {currency}</Text>
                                        </div>
                                    )}
                                    {item.extras && item.extras.length > 0 && (
                                        <div className="w-full flex flex-row justify-between px-2 mt-1">
                                            <Text type="subtitle">Extras: {item.extras.map(extra => extra.name).join(", ")}</Text>
                                            <Text type="subtitle">+{item.extras.reduce((sum, extra) => sum + extra.price, 0).toFixed(2)} {currency}</Text>
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    ))}
                    {order.get('coupons').length > 0 && (
                        <div className="p-2 border-t border-black flex flex-col">
                            <Text type="orderitem" classNames={"font-bold mb-1"}>Angebote:</Text>
                            {order.get('coupons').map((coupon, index) => (
                                <div key={index} className="flex flex-col justify-between">
                                    <div className="w-full flex justify-between">
                                        <Text type="orderitem">{coupon.offerCode}</Text>
                                        <Text type="orderitem">{coupon.offerPrice} {currency}</Text>
                                    </div>
                                    {coupon.offerSnapshotProducts.map((item, idx) => (
                                        <Text key={idx} type="orderitem" classNames="ml-4">- {item.name} x{item.count}</Text>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    {order.get('discount') !== null && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Rabatt:</Text>
                            <Text type="orderitem" classNames={"font-bold"}>{order.get('discount').percentage} %</Text>
                        </div>
                    )}
                    {order.get('totalPrice') !== null && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Gesamt:</Text>
                            <Text type="orderitem" classNames={"font-bold"}>{order.get('totalPrice').toFixed(2)} {currency}</Text>
                        </div>
                    )}
                    {order.get('status') != null && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Status:</Text>
                            <Text type="orderitem" classNames={"font-bold"}>{getStatusLabel()}</Text>
                        </div>
                    )}
                    {order.get('paymentType') !== null && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Bezahlt:</Text>
                            <Text type="orderitem" classNames={"font-bold"}>{getPaymentLabel()}</Text>
                        </div>
                    )}
                    {order.get('comment') && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Kommentar:</Text>
                            <Text type="orderitem">{order.get('comment')}</Text>
                        </div>
                    )}
                    {order.get('preorderName') && (
                        <div className="p-2 border-t border-black flex justify-between">
                            <Text type="orderitem" classNames={"font-bold"}>Vorbesteller Name:</Text>
                            <Text type="orderitem">{order.get('preorderName')}</Text>
                        </div>
                    )}
                </>
            )}
        </>
    )

    async function urlCreator(){
        let offlineId = order.get('offlineId');
        if(!offlineId){
            offlineId = await getOrderById(order.get('orderId')).then((res) => res.offlineId);
        }
        return `${window.location.origin}/bon/${workspace.id}/${offlineId}`;
    }

    const orderSummary = () => (
        <div className="w-full h-full flex flex-col overflow-hidden" onClick={() => handleOutsideClick()}>
            <div className="w-full h-fit flex flex-row justify-between">
                <Text type="itemmain" classNames="mb-2">Bestellübersicht</Text>
                {(order.get('status') === "PENDING" || order.get('status') === "COMPLETE") && (
                    <Button tIconName={"receipt-2"} classNames="w-10 py-1 text-md h-10" buttonColor="bg-gray-100" onClickAction={async () => openModal(<QRCodeGenerator dataString={await urlCreator()} />)} />
                )}
            </div>
            
            <div className="h-full overflow-y-auto flex flex-col">
                {orderListItem()}
            </div>
        </div>
    );

    const orderLayout = () => (
        <div className={`bg-gray-100 ${isPortrait ? 'w-full h-2/3' : 'h-full'} w-full p-2 flex flex-col`}>
            <div className="bg-white rounded-xl border border-gray-300 flex flex-col h-full w-full p-3">
                <div className="flex flex-grow h-auto overflow-y-auto">
                    {orderSummary()}
                </div>

                <div className="h-10 text-center border border-gray-300 w-full rounded-lg bg-gray-100">
                    <Text type="itemmain">{inputValue}</Text>
                </div>

                <div className="w-full flex flex-row h-fit p-1 gap-1 bg-white">
                    <Button iconName={"arrow_left.svg"} classNames="w-10 py-1 text-md h-10" buttonColor="bg-yellow-200" onClickAction={() => handleOldOrdersClick(true)} />
                    <Button text={"Neu"} classNames="w-full py-1 text-md" buttonColor="bg-yellow-200" onClickAction={() => resetOrder()} />
                    <RoleGuard>
                        {order.get('status') && order.get('status') !== "WASTE" && order.get('status') !== "CANCELED" && (
                            <Button text={"Stornieren"} classNames="w-full py-1 text-md" buttonColor="bg-red-500" onClickAction={() => handleCancelOrder()} />
                        )}
                    </RoleGuard>
                    <Button iconName={"arrow_right.svg"} classNames="w-10 py-1 text-md h-10" buttonColor="bg-yellow-200" onClickAction={() => handleOldOrdersClick(false)} />
                </div>

                <div className="w-full h-fit flex flex-col bg-white">
                    {registerInputTerminal()}
                </div>
            </div>

        </div>
    );

    const MainView = () => (
        <div className="w-full h-full flex flex-col gap-2">
            <div className={`h-full flex ${isPortrait ? 'flex-col w-full' : 'flex-row'} gap-1`}>
                {productTerminal()}
                {orderLayout()}
            </div>
        </div>
    );

    return (
        <div className="w-full h-full bg-gray-100 p-1">
            {MainView()}
            <ModalView classNames={isLoginModal ? "w-full h-full" : "w-auto"} hasCloseButton={!isLoginModal} />
            <Snackbar message={snackbarMessage} onClose={() => setSnackbarMessage("")} />
        </div>
    );
}

export default Counter;